import React from 'react';
import moment from 'moment';
import { EnvelopeIcon, DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { formatFileSize, sanitizeEmailHtml } from '../../../utils/formatters';

const UnmatchedEmailPreview = ({ email, onAssign, onIgnore }) => {
  if (!email) {
    return (
      <div className='h-full flex items-center justify-center bg-gray-50'>
        <div className='text-center'>
          <EnvelopeIcon className='w-12 h-12 text-gray-300 mx-auto mb-3' />
          <p className='text-gray-500'>Selecciona un email para ver los detalles</p>
        </div>
      </div>
    );
  }

  const hasHtmlBody = email.body_html && email.body_html.trim() !== '';
  const bodyContent = hasHtmlBody ? email.body_html : email.body_text || '';

  const sanitizedHtml = hasHtmlBody ? sanitizeEmailHtml(bodyContent) : null;

  const isPending = email.status === 'pending';

  return (
    <div className='h-full flex flex-col bg-white'>
      {/* Header */}
      <div className='p-4 border-b border-gray-200'>
        <div className='flex items-start justify-between gap-4'>
          <div className='flex-1 min-w-0'>
            <h2 className='text-lg font-semibold text-gray-800 truncate'>
              {email.subject || '(Sin asunto)'}
            </h2>
            <div className='mt-2 space-y-1'>
              <p className='text-sm text-gray-600'>
                <span className='font-medium'>De:</span>{' '}
                {email.from_name ? `${email.from_name} <${email.from_email}>` : email.from_email}
              </p>
              <p className='text-sm text-gray-600'>
                <span className='font-medium'>Para:</span> {email.to_email}
              </p>
              <p className='text-sm text-gray-500'>
                {moment(email.received_at).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
          </div>

          {/* Actions */}
          {isPending && (
            <div className='flex items-center gap-2 flex-shrink-0'>
              <button
                onClick={() => onIgnore(email._id)}
                className='px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors'
              >
                Ignorar
              </button>
              <button
                onClick={() => onAssign(email)}
                className='px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors'
              >
                Asignar
              </button>
            </div>
          )}

          {!isPending && (
            <div className='flex-shrink-0'>
              <span
                className={`px-3 py-1 text-sm font-medium rounded-full ${
                  email.status === 'assigned'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {email.status === 'assigned' ? 'Asignado' : 'Ignorado'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className='flex-1 overflow-y-auto p-4'>
        {sanitizedHtml ? (
          <div
            className='prose prose-sm max-w-none text-gray-700 [&_a]:text-blue-600 [&_a]:underline'
            dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
          />
        ) : (
          <p className='text-sm text-gray-700 whitespace-pre-wrap'>
            {bodyContent || '(Sin contenido)'}
          </p>
        )}
      </div>

      {/* Attachments */}
      {email.attachments && email.attachments.length > 0 && (
        <div className='p-4 border-t border-gray-200 bg-gray-50'>
          <p className='text-sm font-medium text-gray-700 mb-2'>
            Adjuntos ({email.attachments.length})
          </p>
          <div className='space-y-2'>
            {email.attachments.map((attachment, index) => {
              const isImage = attachment.mime_type?.startsWith('image/');
              const Icon = isImage ? PhotoIcon : DocumentIcon;

              return (
                <div
                  key={`${attachment.filename}-${index}`}
                  className='flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200'
                >
                  <Icon className='w-5 h-5 text-gray-400 flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm text-gray-700 truncate'>{attachment.filename}</p>
                    <p className='text-xs text-gray-500'>
                      {attachment.mime_type}
                      {attachment.size && ` - ${formatFileSize(attachment.size)}`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnmatchedEmailPreview;
